import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/BillingPage.css';
import InvoicePreview from '../components/InvoicePreview';
import qrCodeImg from '../assets/google-review-qr.webp';

// --- Icons (using simple buttons if icons missing, but keeping structure) ---
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';

const BillingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // --- 1. Gold & Silver Rate Context ---
    const [goldRate, setGoldRate] = useState(0);
    const [silverRate, setSilverRate] = useState(78); // Default Silver Rate
    const [goldRatePurity, setGoldRatePurity] = useState('22K');
    const [rateDate, setRateDate] = useState(new Date());

    // --- 2. Data State ---
    const [customers, setCustomers] = useState([]);
    const [inventory, setInventory] = useState([]);

    // --- 3. Customer State ---
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', phone: '', address: { city: '' } });

    // --- 4. Item Entry State ---
    const [itemMode, setItemMode] = useState('inventory');
    const [inventorySearch, setInventorySearch] = useState('');
    const [showInventoryDropdown, setShowInventoryDropdown] = useState(false);

    const [currentItem, setCurrentItem] = useState({
        name: '',
        metalType: 'Gold', // 'Gold' | 'Silver'
        huid: '',
        purity: '22K',
        weight: '',        // Gross
        stoneWeight: '',
        rate: goldRate,
        makingChgType: 'flat',
        makingChgValue: '',
        stoneCharges: '',
        quantity: 1
    });

    // --- 5. Cart/Bill State ---
    const [billItems, setBillItems] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [includeGst, setIncludeGst] = useState(true); // New state for GST toggle

    // Payment
    const [paymentMode, setPaymentMode] = useState('Cash');
    const [paymentStatus, setPaymentStatus] = useState('Paid');
    const [advanceAmount, setAdvanceAmount] = useState('');
    const [notes, setNotes] = useState('');

    // Refs
    // const invInputRef = useRef(null);

    // --- Effects ---
    useEffect(() => {
        fetchCustomers();
        fetchInventory();
        fetchLiveGoldRate();

        // Check for navigation state (Pre-fill Customer)
        if (location.state?.selectedCustomer) {
            setSelectedCustomer(location.state.selectedCustomer);
        }
    }, [location.state]);

    const fetchLiveGoldRate = async () => {
        try {
            // Mocking the API response from LiveGoldRate.js
            const mockRateData = {
                gold995: { sell: '104185' } // 10g price
            };
            const price10g = parseFloat(mockRateData.gold995.sell.replace(/,/g, ''));
            const price1g24k = price10g / 10;
            const price1g22k = Math.round(price1g24k * 0.916);
            setGoldRate(price1g22k);
            setRateDate(new Date());
        } catch (err) {
            console.error("Failed to fetch rates", err);
        }
    };

    const fetchCustomers = async () => {
        try { const res = await axios.get('/api/customers'); setCustomers(res.data); } catch (e) { }
    };
    const fetchInventory = async () => {
        try { const res = await axios.get('/api/jewellery?isAdmin=true'); setInventory(res.data); } catch (e) { }
    };

    // Update rate when metal type changes
    useEffect(() => {
        if (currentItem.metalType === 'Gold') {
            setCurrentItem(prev => ({ ...prev, rate: goldRate, purity: '22K' }));
        } else {
            setCurrentItem(prev => ({ ...prev, rate: silverRate, purity: '999', stoneWeight: '' })); // Reset stone wt for silver
        }
    }, [currentItem.metalType, goldRate, silverRate]);

    // --- Logic: Calculations ---
    const calculateItemTotals = (item) => {
        const grossWt = parseFloat(item.weight) || 0;
        let stoneWt = parseFloat(item.stoneWeight) || 0;

        // SILVER LOGIC: Net = Gross (Ignore stone weight for billing usually, or user sets it)
        // User requirement: "When Metal = Silver: Auto-hide Stone Weight, Net weight = Gross weight"
        if (item.metalType === 'Silver') {
            stoneWt = 0;
        }

        const netWt = Math.max(0, grossWt - stoneWt);
        const rate = parseFloat(item.rate) || 0;
        const qty = parseInt(item.quantity) || 1;
        const makingVal = parseFloat(item.makingChgValue) || 0;
        const stoneVal = parseFloat(item.stoneCharges) || 0;

        const valMetal = netWt * rate; // 'Gold Value' or 'Silver Value'

        let makingCharges = 0;
        if (item.makingChgType === 'percent') {
            makingCharges = Math.round(valMetal * (makingVal / 100));
        } else {
            makingCharges = makingVal;
        }

        const unitTotal = valMetal + makingCharges + stoneVal;
        const lineTotal = unitTotal * qty;

        return {
            grossWeight: grossWt,
            stoneWeight: stoneWt,
            netWeight: netWt,
            baseValue: valMetal,
            makingCharges: Math.round(makingCharges),
            unitTotal: Math.round(unitTotal),
            lineTotal: Math.round(lineTotal)
        };
    };

    // ... (Handlers) ...

    const handleInventorySelect = (invItem) => {
        setInventorySearch(invItem.name);
        const isSilver = invItem.metal_type === 'Silver';
        setCurrentItem({
            ...currentItem,
            name: invItem.name,
            metalType: invItem.metal_type || 'Gold',
            huid: invItem.huid || '',
            weight: invItem.weight,
            stoneWeight: invItem.weight_stone || 0,
            rate: isSilver ? silverRate : goldRate,
            makingChgType: 'flat',
            makingChgValue: '',
            stoneCharges: '',
            quantity: 1,
            linkedId: invItem._id
        });
        setShowInventoryDropdown(false);
    };

    const addItemToBill = () => {
        let finalName = currentItem.name;
        if (!finalName && itemMode === 'inventory' && inventorySearch) {
            finalName = inventorySearch;
        }

        if (!finalName || !currentItem.weight || !currentItem.rate) {
            alert("Please enter Name, Gross Weight and Rate");
            return;
        }

        const itemToProcess = { ...currentItem, name: finalName };
        const calcs = calculateItemTotals(itemToProcess);

        const newItem = {
            ...itemToProcess,
            ...calcs
        };
        setBillItems([...billItems, newItem]);

        // Reset
        const isGold = currentItem.metalType === 'Gold';
        setCurrentItem({
            name: '', metalType: currentItem.metalType, purity: currentItem.purity, huid: '',
            weight: '', stoneWeight: '', rate: isGold ? goldRate : silverRate,
            makingChgType: 'flat', makingChgValue: '', stoneCharges: '', quantity: 1
        });
        setInventorySearch('');
    };

    // ... (Render) ...
    // Update Header for Dual Rates
    /* 
       <div className="gold-rate-card"> ... 
         Gold: ... Silver: ... 
       </div> 
    */

    // Update Item Entry Grid
    /*
       Col 1: Search/Name
       Col 2: Metal Type (NEW)
       Col 3: Gross Wt
       ...
    */

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            addItemToBill();
        }
    };

    const removeItem = (idx) => {
        const newItems = [...billItems];
        newItems.splice(idx, 1);
        setBillItems(newItems);
    };

    // --- Logic: Bill Summary ---
    const subtotal = billItems.reduce((sum, i) => sum + i.lineTotal, 0);

    // Tax Logic: 3% Total (1.5% CGST + 1.5% SGST)
    // Tax Logic: 3% Total (1.5% CGST + 1.5% SGST) if enabled
    const gstTotal = includeGst ? Math.round(subtotal * 0.03) : 0;
    const cgst = Math.round(subtotal * 0.015);
    const sgst = gstTotal - cgst; // Avoiding rounding diffs by subtracting

    const grandTotal = subtotal + gstTotal - Number(discount);

    const balanceDue = paymentStatus === 'Paid' ? 0
        : Math.max(0, grandTotal - (Number(advanceAmount) || 0));

    // --- Handlers: Item Entry ---
    const saveNewCustomerInternal = async () => {
        try {
            const res = await axios.post('/api/customers', {
                ...newCustomer,
                isActive: true,
                createdAt: new Date()
            });
            setCustomers([...customers, res.data]);

            setSelectedCustomer(res.data);
            setIsNewCustomer(false);
            setShowCustomerModal(false); // Close Modal
            setNewCustomer({ firstName: '', lastName: '', phone: '', address: { city: '' } }); // Reset form
        } catch (e) { alert('Error saving customer'); }
    };

    // --- Handlers: Save Bill ---
    const { user } = useAuth(); // Get logged in user

    const handleSaveBill = async (status = 'Final') => {
        if (!selectedCustomer) return alert('Select Customer');
        if (billItems.length === 0) return alert('Add items');

        const billPayload = {
            customer: selectedCustomer,
            items: billItems.map(item => ({
                ...item,
                price: item.unitTotal, // Backend requires fields 'price' and 'quantity'
                total: item.lineTotal,
                // Ensure defaults for specific fields if needed
                metalType: item.metalType || 'Gold',
                purity: item.purity || '22K',
                huid: item.huid || '',
                grossWeight: item.grossWeight || 0,
                netWeight: item.netWeight || 0
            })),
            subtotal,
            taxBreakup: { cgst, sgst, total: gstTotal },
            gst: gstTotal,
            discount: Number(discount),
            subtotal,
            taxBreakup: { cgst, sgst, total: gstTotal },
            gst: gstTotal,
            includeGst, // Pass this to backend if needed
            discount: Number(discount),
            totalAmount: grandTotal,
            paymentDetails: {
                mode: paymentMode,
                status: status === 'Draft' ? 'Draft' : paymentStatus,
                advance: Number(advanceAmount) || 0,
                balance: balanceDue
            },
            goldRateContext: { rate: goldRate, purity: goldRatePurity, date: rateDate },
            salesperson: user?.firstName || 'Admin',
            notes,
            createdAt: new Date()
        };

        try {
            await axios.post('/api/bills', billPayload);
            alert(`Bill ${status === 'Draft' ? 'Saved as Draft' : 'Generated'} Successfully!`);
            // If final, maybe offer to print?
            if (status === 'Final') {
                // Could redirect to history or show preview again
                navigate('/billing/history');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to save bill');
        }
    };

    // --- Preview & Print ---
    const [showPreview, setShowPreview] = useState(false);

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="billing-container">

            {/* 1. Header & Gold Rate */}
            <div className="billing-header">
                <div className="billing-title-section">
                    <h2>New Tax Invoice</h2>
                    <div className="billing-meta">
                        Invoice #: <strong>AUTO</strong> | Date: {new Date().toLocaleDateString()}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {/* History Button */}
                    <button className="btn-action btn-outline" onClick={() => navigate('/billing/history')}>
                        History
                    </button>

                    <div className="gold-rate-card">
                        <div className="gold-rate-info">
                            <span className="gold-rate-label">Today's Gold Rate (22K)</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ₹ <input
                                    type="number"
                                    value={goldRate}
                                    onChange={e => setGoldRate(e.target.value)}
                                    style={{
                                        background: 'transparent', border: 'none', borderBottom: '1px dashed #d4ac0d',
                                        width: '100px', fontSize: '16px', fontWeight: '700', color: '#d4ac0d'
                                    }}
                                /> / gm
                            </div>
                            <small style={{ fontSize: '10px', color: '#856404' }}>Updated: {rateDate.toLocaleTimeString()}</small>
                        </div>
                    </div>
                </div>
            </div>

            <div className="billing-grid">

                {/* LEFT: Item Entry & Table */}
                <div className="billing-main">

                    {/* Item Entry Form */}
                    <div className="billing-card">
                        <span className="card-title">Add Item</span>

                        <div className="item-type-toggle">
                            <button
                                className={`toggle-btn ${itemMode === 'inventory' ? 'active' : ''}`}
                                onClick={() => setItemMode('inventory')}
                            >
                                Inventory
                            </button>
                            <button
                                className={`toggle-btn ${itemMode === 'custom' ? 'active' : ''}`}
                                onClick={() => { setItemMode('custom'); setInventorySearch(''); }}
                            >
                                Custom
                            </button>
                        </div>

                        {/* Row 1: Search, Metal, Gross, [Stone], Net, Rate */}
                        <div style={{ display: 'grid', gridTemplateColumns: currentItem.metalType === 'Gold' ? '1.8fr 0.8fr 1fr 1fr 0.8fr 1fr' : '1.8fr 0.8fr 1fr 0.8fr 1fr', gap: '10px', alignItems: 'end' }}>
                            {itemMode === 'inventory' ? (
                                <div className="form-group">
                                    <label>Search Item</label>
                                    <input
                                        className="form-control"
                                        placeholder="Search item (Ring, Necklace, SKU)"
                                        value={inventorySearch}
                                        onChange={(e) => { setInventorySearch(e.target.value); setShowInventoryDropdown(true); }}
                                    />
                                    {showInventoryDropdown && inventorySearch && (
                                        <div className="autocomplete-dropdown">
                                            {inventory.filter(i => i.name.toLowerCase().includes(inventorySearch.toLowerCase()))
                                                .map(i => (
                                                    <div key={i._id} className="autocomplete-item" onClick={() => handleInventorySelect(i)}>
                                                        {i.name} ({i.metal_type})
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>Item Name</label>
                                    <input
                                        className="form-control"
                                        value={currentItem.name}
                                        onChange={e => setCurrentItem({ ...currentItem, name: e.target.value })}
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label>Metal</label>
                                <select
                                    className="form-control"
                                    value={currentItem.metalType}
                                    onChange={e => setCurrentItem({ ...currentItem, metalType: e.target.value })}
                                >
                                    <option value="Gold">Gold</option>
                                    <option value="Silver">Silver</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>{currentItem.metalType === 'Silver' ? 'Weight (g)' : 'Gross Wt'}</label>
                                <input
                                    type="number" className="form-control"
                                    value={currentItem.weight}
                                    onChange={e => setCurrentItem({ ...currentItem, weight: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>

                            {/* Stone Wt only for Gold (as per simple requirement) */}
                            {currentItem.metalType === 'Gold' && (
                                <div className="form-group">
                                    <label>Stone Wt</label>
                                    <input
                                        type="number" className="form-control"
                                        value={currentItem.stoneWeight}
                                        onChange={e => setCurrentItem({ ...currentItem, stoneWeight: e.target.value })}
                                        onKeyDown={handleKeyDown}
                                        placeholder="0"
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label style={{ color: '#d32f2f' }}>Net Wt</label>
                                <div style={{ padding: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                    {(parseFloat(currentItem.metalType === 'Gold' ? (parseFloat(currentItem.weight || 0) - parseFloat(currentItem.stoneWeight || 0)) : currentItem.weight || 0).toFixed(2))}g
                                </div>
                            </div>

                            <div className="form-group" style={{ flex: '0.8' }}>
                                <label>Rate (₹/g)</label>
                                <input
                                    type="number" className="form-control"
                                    value={currentItem.rate}
                                    onChange={e => setCurrentItem({ ...currentItem, rate: e.target.value })}
                                    onKeyDown={handleKeyDown}
                                />
                                <small style={{ fontSize: '9px', color: '#666' }}>
                                    Locked {rateDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>
                            </div>

                            <div className="form-group" style={{ flex: '0.8' }}>
                                <label>HUID</label>
                                <input
                                    className="form-control"
                                    value={currentItem.huid}
                                    onChange={e => setCurrentItem({ ...currentItem, huid: e.target.value })}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        {/* Row 2: Making, Stone/Other, Qty, Add Button */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.5fr 100px', gap: '12px', alignItems: 'flex-start', marginTop: '12px' }}>
                            <div className="form-group">
                                <label>Making Charges</label>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <select
                                        className="form-control" style={{ width: '60px' }}
                                        value={currentItem.makingChgType}
                                        onChange={e => setCurrentItem({ ...currentItem, makingChgType: e.target.value })}
                                    >
                                        <option value="flat">₹</option>
                                        <option value="percent">%</option>
                                    </select>
                                    <input
                                        type="number" className="form-control" placeholder="Value"
                                        value={currentItem.makingChgValue}
                                        onChange={e => setCurrentItem({ ...currentItem, makingChgValue: e.target.value })}
                                    />
                                </div>
                                <small>
                                    Calc: ₹{calculateItemTotals(currentItem).makingCharges || 0} <br />
                                    <span style={{ opacity: 0.7 }}>Flat or per gram based on selection</span>
                                </small>
                            </div>

                            <div className="form-group">
                                <label>Stone/Other</label>
                                <input
                                    type="number" className="form-control" placeholder="Values (₹)"
                                    value={currentItem.stoneCharges}
                                    onChange={e => setCurrentItem({ ...currentItem, stoneCharges: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Qty</label>
                                <input
                                    type="number" className="form-control"
                                    value={currentItem.quantity}
                                    onChange={e => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>&nbsp;</label>
                                <button className="add-btn" onClick={addItemToBill}>+ Add Item</button>
                            </div>
                        </div>

                        <hr className="divider-line" />

                    </div>

                    {/* Items Table */}
                    <div className="billing-card" style={{ flex: 1 }}>
                        <div className="items-table-container">
                            <table className="items-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Item</th>
                                        <th>HUID</th>
                                        <th>Weight Details</th>
                                        <th>Rate</th>
                                        <th>Making</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billItems.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <strong>{item.name}</strong>
                                            </td>
                                            <td style={{ fontFamily: 'monospace', color: '#555', fontSize: '12px' }}>{item.huid || '-'}</td>
                                            <td>
                                                <div>Net: <strong>{item.netWeight}g</strong></div>
                                                <span className="table-subtext">Gross: {item.grossWeight}g | Stone: {item.stoneWeight}g</span>
                                            </td>
                                            <td>₹{item.rate}</td>
                                            <td>
                                                <div>₹{item.makingCharges}</div>
                                                <span className="table-subtext">{item.makingChgType === 'percent' ? `@ ${item.makingChgValue}%` : 'Flat'}</span>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>₹{item.lineTotal.toLocaleString()}</td>
                                            <td>
                                                <DeleteIcon
                                                    style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '18px' }}
                                                    onClick={() => removeItem(idx)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    {billItems.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: '#666', fontStyle: 'italic' }}>No items added yet. Add an item above to generate bill.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* RIGHT: Customer & Summary */}
                <div className="billing-sidebar">

                    {/* Customer */}
                    <div className="billing-card customer-scroll-container">
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span className="card-title">Customer</span>
                            <span
                                style={{ color: 'var(--primary-main)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                onClick={() => { setShowCustomerModal(true); setIsNewCustomer(false); setSelectedCustomer(null); }}
                            >
                                + New Customer
                            </span>
                        </div>

                        <>
                            {!selectedCustomer ? (
                                <div className="customer-search-box">
                                    <input
                                        className="form-control" placeholder="Search (Name/Phone)"
                                        value={customerSearch}
                                        onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                                    />
                                    {showCustomerDropdown && customerSearch && (
                                        <div className="autocomplete-dropdown">
                                            {customers.filter(c => c.firstName.toLowerCase().includes(customerSearch.toLowerCase()))
                                                .map(c => (
                                                    <div key={c.id} className="autocomplete-item" onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); }}>
                                                        {c.firstName} {c.lastName} ({c.phone})
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="customer-bill-panel">
                                    <div className="bill-row">
                                        <span className="bill-label">Name</span>
                                        <span className="bill-value">{selectedCustomer.firstName} {selectedCustomer.lastName}</span>
                                    </div>
                                    <div className="bill-row">
                                        <span className="bill-label">Phone</span>
                                        <span className="bill-value">{selectedCustomer.phone}</span>
                                    </div>
                                    <div className="bill-row">
                                        <span className="bill-label">City</span>
                                        <span className="bill-value">{selectedCustomer.address?.city || 'N/A'}</span>
                                    </div>
                                    <div className="bill-row">
                                        <span className="bill-label">ID</span>
                                        <span className="bill-value" style={{ fontFamily: 'monospace' }}>
                                            {selectedCustomer._id ? `CUST-${selectedCustomer._id.slice(-4).toUpperCase()}` : 'NEW'}
                                        </span>
                                    </div>

                                    <div style={{ marginTop: '12px', textAlign: 'right' }}>
                                        <span
                                            className="link-action"
                                            onClick={() => setSelectedCustomer(null)}
                                            style={{ fontSize: '11px', color: '#d32f2f', textDecoration: 'underline', cursor: 'pointer' }}
                                        >
                                            Change Customer
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>

                    </div>

                    {/* Payment & Summary */}
                    <div className="billing-card">
                        <span className="card-title">Payment & Tax</span>

                        <div className="summary-section">
                            <div className="summary-row subtotal">
                                <span>Subtotal</span>
                                <span>₹{subtotal.toLocaleString()}</span>
                            </div>

                            <div className="summary-row" style={{ alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '13px', color: '#555' }}>Include GST (3%)</span>
                                <input
                                    type="checkbox"
                                    checked={includeGst}
                                    onChange={e => setIncludeGst(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                            </div>

                            {includeGst && (
                                <>
                                    <div className="summary-row tax">
                                        <span>CGST (1.5%)</span>
                                        <span>₹{cgst.toLocaleString()}</span>
                                    </div>
                                    <div className="summary-row tax">
                                        <span>SGST (1.5%)</span>
                                        <span>₹{sgst.toLocaleString()}</span>
                                    </div>
                                </>
                            )}

                            <div className="summary-row">
                                <span>Discount</span>
                                <input
                                    type="number" style={{ width: '80px', textAlign: 'right', padding: '4px' }}
                                    value={discount} onChange={e => setDiscount(e.target.value)}
                                />
                            </div>

                            <div className="summary-row grand-total">
                                <span>Grand Total</span>
                                <span>₹{grandTotal.toLocaleString()}</span>
                            </div>
                        </div>

                        <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--divider)' }} />

                        <div className="payment-inputs" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Mode</label>
                                    <select className="form-control" value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
                                        <option>Cash</option>
                                        <option>UPI</option>
                                        <option>Card</option>
                                        <option>Bank Transfer</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select className="form-control" value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                                        <option>Paid</option>
                                        <option>Partial</option>
                                        <option>Pending</option>
                                    </select>
                                </div>
                            </div>

                            {paymentStatus === 'Partial' && (
                                <div className="form-group">
                                    <label>Advance Amount</label>
                                    <input type="number" className="form-control" value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} placeholder="0.00" />
                                    <small style={{ color: '#d32f2f', fontWeight: 600 }}>Bal: ₹{balanceDue.toLocaleString()}</small>
                                </div>
                            )}
                        </div>

                        <div className="form-group" style={{ marginTop: '8px' }}>
                            <label>Notes</label>
                            <textarea
                                className="form-control" style={{ height: '50px', resize: 'none' }}
                                placeholder="Remarks..."
                                value={notes} onChange={e => setNotes(e.target.value)}
                            />
                        </div>

                        <div className="action-grid">
                            <button className="btn-action btn-outline" onClick={() => setShowPreview(true)}>Preview</button>
                            <button className="btn-action btn-gold" onClick={() => handleSaveBill('Final')}>
                                <SaveIcon style={{ fontSize: '18px' }} />
                                Generate Bill
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="modal-overlay" onClick={() => setShowPreview(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <InvoicePreview
                            bill={{
                                billNumber: 'PREVIEW',
                                createdAt: new Date(),
                                customer: selectedCustomer,
                                items: billItems.map(i => ({ ...i, total: i.lineTotal })),
                                subtotal,
                                gst: gstTotal,
                                includeGst,
                                discount: Number(discount),
                                grandTotal: grandTotal,
                                paymentDetails: { mode: paymentMode }
                            }}
                            onClose={() => setShowPreview(false)}
                        />
                    </div>
                </div>
            )}

            {/* Customer Creation Modal */}
            {showCustomerModal && (
                <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '90%' }}>
                        <div className="billing-card" style={{ boxShadow: 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <span className="card-title" style={{ fontSize: '18px', borderBottom: 'none' }}>Add New Customer</span>
                                <button className="btn-action btn-outline" onClick={() => setShowCustomerModal(false)} style={{ padding: '4px 12px' }}>✕</button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div className="form-group">
                                    <label>First Name <span style={{ color: 'red' }}>*</span></label>
                                    <input className="form-control" value={newCustomer.firstName} onChange={e => setNewCustomer({ ...newCustomer, firstName: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Last Name <span style={{ color: 'red' }}>*</span></label>
                                    <input className="form-control" value={newCustomer.lastName} onChange={e => setNewCustomer({ ...newCustomer, lastName: e.target.value })} />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginTop: '12px' }}>
                                <label>Mobile Number <span style={{ color: 'red' }}>*</span></label>
                                <input className="form-control" value={newCustomer.phone} onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })} placeholder="10-digit mobile" />
                            </div>

                            <hr className="divider-line" style={{ margin: '16px 0' }} />

                            <div className="form-group">
                                <label>City / Address</label>
                                <input className="form-control" value={newCustomer.address.city} onChange={e => setNewCustomer({ ...newCustomer, address: { city: e.target.value } })} placeholder="City" />
                            </div>

                            <div style={{ marginTop: '20px' }}>
                                <button className="btn-action btn-primary" style={{ width: '100%' }} onClick={saveNewCustomerInternal}>
                                    Save Customer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default BillingPage;
